import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  FiAward,
  FiCheck,
  FiX,
  FiSearch,
  FiFilter,
  FiUser,
  FiPhone,
  FiCalendar,
  FiStar,
  FiShield,
  FiEye,
  FiTrendingUp,
} from "react-icons/fi";
import { FaCrown, FaGem, FaMedal } from "react-icons/fa";
import { FaChevronCircleLeft } from "react-icons/fa";
import { FaCircleChevronRight } from "react-icons/fa6";
import { useToast } from "@chakra-ui/react";

// Fallback dummy applications for testing and layout preview
const DUMMY_PARTNERS = [
  {
    _id: "part_001",
    fullName: "Mira Sen",
    phoneNumber: "9876543210",
    gender: "female",
    avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=400&q=80",
    status: "pending", // pending | approved | rejected
    appliedAt: "2026-10-02T14:30:00.000Z",
    bio: "Content creator & stream host with 45K social followers.",
    socialHandle: "@miraocean",
    crownType: "Gold",
  },
  {
    _id: "part_002",
    fullName: "Kabir Roy",
    phoneNumber: "9823456789",
    gender: "male",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80",
    status: "approved",
    appliedAt: "2026-09-25T11:15:00.000Z",
    bio: "Community host with consistent weekly engagement records.",
    socialHandle: "@kabir_vibes",
    crownType: "Silver Tide",
  },
  {
    _id: "part_003",
    fullName: "Nandini Das",
    phoneNumber: "9945123890",
    gender: "female",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=400&q=80",
    status: "rejected",
    appliedAt: "2026-09-18T09:40:00.000Z",
    bio: "Aspiring creator looking to grow community reach.",
    socialHandle: "@nandini_d",
    crownType: "Diamond Siren",
  },
];

const CROWN_OPTIONS = [
    { label: "Diamond Siren", value: "Diamond", color: "text-sky-300" },
    { label: "Platinum Royal", value: "Platinum", color: "text-cyan-300" },
    { label: "Gold Abyss", value: "Gold", color: "text-yellow-400" },
    { label: "Silver Tide", value: "Silver", color: "text-slate-300" },
    { label: "Bronze Coral", value: "Bronze", color: "text-amber-500" },
];

export default function Partners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | pending | approved | rejected

  const [selectedPartner, setSelectedPartner] = useState(null);
  const [viewDetailsModal, setViewDetailsModal] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);

  // Assignment Modal Form
  const [assignForm, setAssignForm] = useState({
    crownType: "None",
  });

  const [page, setPage] = useState(1);
  const limit = 10;
  const toast = useToast();

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/v1/admin/partner-applications");
      if (res.data?.success && Array.isArray(res.data.data)) {
        setPartners(res.data.data.length > 0 ? res.data.data : DUMMY_PARTNERS);
      } else {
        setPartners(DUMMY_PARTNERS);
      }
    } catch (_) {
      setPartners(DUMMY_PARTNERS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const filteredPartners = useMemo(() => {
    return partners.filter((item) => {
      const matchSearch =
        item.fullName.toLowerCase().includes(search.toLowerCase()) ||
        item.phoneNumber.includes(search) ||
        (item.socialHandle && item.socialHandle.toLowerCase().includes(search.toLowerCase()));

      const matchStatus = statusFilter === "all" || item.status === statusFilter;

      return matchSearch && matchStatus;
    });
  }, [partners, search, statusFilter]);

  const stats = useMemo(() => {
    const total = partners.length;
    const pending = partners.filter((p) => p.status === "pending").length;
    const approved = partners.filter((p) => p.status === "approved").length;
    const rejected = partners.filter((p) => p.status === "rejected").length;
    return { total, pending, approved, rejected };
  }, [partners]);

  const handleUpdateStatus = async (partnerId, newStatus) => {
    try {
      await axios.patch(`/api/v1/admin/partner-applications/${partnerId}/status`, {
        status: newStatus,
      });
    } catch (_) {
      // Local mutation fallback
    }

    setPartners((prev) =>
      prev.map((p) => (p._id === partnerId ? { ...p, status: newStatus } : p))
    );

    toast({
      title: `Application ${newStatus.toUpperCase()}`,
      status: newStatus === "approved" ? "success" : "warning",
      duration: 2500,
      isClosable: true,
    });
  };

  const handleOpenAssignModal = (partner) => {
    setSelectedPartner(partner);
    setAssignForm({
      crownType: partner.crownType || "None",
    });
    setAssignModalOpen(true);
  };

  const handleSaveAssignments = async () => {
    if (!selectedPartner) return;

    try {
      await axios.patch(`/api/v1/admin/partner-applications/${selectedPartner._id}/badges`, assignForm);
    } catch (_) {
      // Local mutation fallback
    }

    setPartners((prev) =>
      prev.map((p) => (p._id === selectedPartner._id ? { ...p, ...assignForm } : p))
    );

    toast({
      title: "Crown Assigned",
      description: `Updated crown for ${selectedPartner.fullName}`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });

    setAssignModalOpen(false);
  };

  return (
    <div className="space-y-6 text-cyan-50 p-2 md:p-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-2 border-b border-cyan-500/20">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-cyan-200 via-sky-300 to-white flex items-center gap-3">
            <FaCrown className="text-amber-400" />
            <span>Partner Network & Creator Approvals</span>
          </h1>
          <p className="text-xs sm:text-sm text-cyan-200/60 mt-1">
            Review partner requests, grant platform verification, and bestow royal crowns & tier levels
          </p>
        </div>
      </div>

      {/* Metric Cards Ribbon */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-2xl p-4 bg-gradient-to-br from-cyan-500/20 to-blue-600/10 border border-cyan-400/30 backdrop-blur-xl shadow-lg">
          <p className="text-[11px] font-bold uppercase tracking-wider text-cyan-200/70">Total Applicants</p>
          <p className="text-2xl font-black text-cyan-300 mt-1">{stats.total}</p>
        </div>
        <div className="rounded-2xl p-4 bg-gradient-to-br from-amber-500/20 to-orange-500/10 border border-amber-400/30 backdrop-blur-xl shadow-lg">
          <p className="text-[11px] font-bold uppercase tracking-wider text-amber-200/70">Pending Review</p>
          <p className="text-2xl font-black text-amber-300 mt-1">{stats.pending}</p>
        </div>
        <div className="rounded-2xl p-4 bg-gradient-to-br from-teal-500/20 to-emerald-500/10 border border-teal-400/30 backdrop-blur-xl shadow-lg">
          <p className="text-[11px] font-bold uppercase tracking-wider text-teal-200/70">Approved Partners</p>
          <p className="text-2xl font-black text-teal-300 mt-1">{stats.approved}</p>
        </div>
        <div className="rounded-2xl p-4 bg-gradient-to-br from-rose-500/20 to-pink-500/10 border border-rose-400/30 backdrop-blur-xl shadow-lg">
          <p className="text-[11px] font-bold uppercase tracking-wider text-rose-200/70">Rejected</p>
          <p className="text-2xl font-black text-rose-300 mt-1">{stats.rejected}</p>
        </div>
      </div>

      {/* Main Glassmorphic Container */}
      <div
        className="rounded-2xl p-6 backdrop-blur-xl border border-cyan-500/20 space-y-6"
        style={{
          background: "linear-gradient(160deg, rgba(6, 24, 60, 0.75) 0%, rgba(3, 13, 36, 0.88) 100%)",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.45)",
        }}
      >
        {/* Search & Filters */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="relative w-full md:w-80">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-cyan-400 text-sm">
              <FiSearch />
            </span>
            <input
              type="text"
              placeholder="Search partner by name, phone or handle..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-cyan-400/30 bg-cyan-950/70 text-cyan-50 text-xs focus:outline-none focus:border-cyan-300 shadow-inner"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3.5 py-2 rounded-xl border border-cyan-400/30 bg-cyan-950/70 text-cyan-100 text-xs focus:outline-none focus:border-cyan-300"
            >
              <option value="all">All Application Status</option>
              <option value="pending">Pending Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Partners Feed / Cards List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 rounded-full border-4 border-cyan-500/20 border-t-cyan-400 animate-spin" />
          </div>
        ) : filteredPartners.length === 0 ? (
          <div className="text-center py-16 text-cyan-200/50 text-xs">
            No partner applications match the selected criteria.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredPartners.map((user) => {
              const isApproved = user.status === "approved";
              const isPending = user.status === "pending";

              return (
                <div
                  key={user._id}
                  className="rounded-2xl p-5 border border-cyan-500/20 bg-cyan-950/35 backdrop-blur-md relative flex flex-col justify-between hover:border-cyan-400/50 hover:bg-cyan-900/30 transition-all duration-300 shadow-xl"
                >
                  <div>
                    {/* Crown & Status Header */}
                    <div className="flex items-center justify-between mb-3">
                      <span
                        className={`text-[10px] uppercase font-extrabold px-2 py-0.5 rounded-full border ${
                          isApproved
                            ? "bg-teal-500/15 border-teal-400/30 text-teal-300"
                            : user.status === "rejected"
                            ? "bg-rose-500/15 border-rose-400/30 text-rose-300"
                            : "bg-amber-500/15 border-amber-400/30 text-amber-300"
                        }`}
                      >
                        {user.status}
                      </span>
                    </div>

                    {/* Member Profile Avatar & Info */}
                    <div className="flex items-start gap-3.5 mb-4">
                      <div className="relative">
                        <img
                          src={user.avatar || "/default-avatar.png"}
                          alt={user.fullName}
                          className="w-14 h-14 rounded-2xl object-cover border border-cyan-400/30 shadow-md"
                        />
                        {isApproved && (
                          <div className="absolute -bottom-1 -right-1 bg-cyan-400 text-black rounded-full p-0.5 border border-black shadow">
                            <FiCheck size={11} className="stroke-[3]" />
                          </div>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <h3 className="font-bold text-white text-base truncate">{user.fullName}</h3>
                        <p className="text-xs text-cyan-300/80 font-mono">{user.phoneNumber}</p>
                        <p className="text-[11px] text-cyan-400/60 mt-0.5 truncate">{user.socialHandle}</p>
                      </div>
                    </div>

                    {/* Crown Badge */}
                    <div className="p-3 rounded-xl bg-cyan-950/60 border border-cyan-500/15 space-y-1.5 text-xs mb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-cyan-300/70 font-medium">Crown Badge:</span>
                        <span className="font-bold text-cyan-200 flex items-center gap-1.5">
                          <FaCrown
                            className={
                              user.crownType === "Gold"
                                ? "text-yellow-400"
                                : user.crownType === "Platinum"
                                ? "text-cyan-300"
                                : user.crownType === "Diamond"
                                ? "text-sky-300"
                                : user.crownType === "Silver"
                                ? "text-slate-300"
                                : user.crownType === "Bronze"
                                ? "text-amber-500"
                                : "text-gray-400"
                            }
                          />
                          {user.crownType || "None"}
                        </span>
                      </div>
                    </div>

                    {user.bio && (
                      <p className="text-[11px] text-cyan-200/70 line-clamp-2 italic mb-4">"{user.bio}"</p>
                    )}
                  </div>

                  {/* Actions & Decision Tools */}
                  <div className="pt-3 border-t border-cyan-500/20 space-y-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedPartner(user);
                          setViewDetailsModal(true);
                        }}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold text-cyan-200 bg-cyan-950/70 border border-cyan-500/30 hover:bg-cyan-900/60 transition"
                      >
                        <FiEye size={13} /> View Profile
                      </button>

                      <button
                        onClick={() => handleOpenAssignModal(user)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-amber-500 to-orange-600 hover:brightness-110 shadow-md transition"
                      >
                        <FaMedal size={13} /> Assign Badge
                      </button>
                    </div>

                    {isPending && (
                      <div className="flex items-center gap-2 pt-1">
                        <button
                          onClick={() => handleUpdateStatus(user._id, "approved")}
                          className="flex-1 py-1.5 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-teal-500 to-emerald-600 hover:brightness-110 shadow transition"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleUpdateStatus(user._id, "rejected")}
                          className="flex-1 py-1.5 rounded-xl text-xs font-bold text-rose-200 bg-rose-950/60 border border-rose-500/30 hover:bg-rose-900/60 transition"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* MODAL 1: VIEW PROFILE DETAILS */}
      {viewDetailsModal && selectedPartner && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
          onClick={() => setViewDetailsModal(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl overflow-hidden border border-cyan-400/30 bg-[#041230] p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center pb-4 border-b border-cyan-500/20">
              <h3 className="text-base font-bold text-white">Partner Application Profile</h3>
              <button onClick={() => setViewDetailsModal(false)} className="text-cyan-300 hover:text-white">
                <FiX size={18} />
              </button>
            </div>

            <div className="py-4 space-y-4 text-xs">
              <div className="flex items-center gap-3">
                <img
                  src={selectedPartner.avatar || "/default-avatar.png"}
                  alt={selectedPartner.fullName}
                  className="w-16 h-16 rounded-2xl object-cover border border-cyan-400/40"
                />
                <div>
                  <h4 className="text-base font-bold text-white">{selectedPartner.fullName}</h4>
                  <p className="text-cyan-300 font-mono">{selectedPartner.phoneNumber}</p>
                  <p className="text-cyan-400/70">{selectedPartner.socialHandle}</p>
                </div>
              </div>

              <div className="p-3.5 rounded-xl bg-cyan-950/50 border border-cyan-500/20 space-y-2">
                <div>
                  <span className="text-cyan-300/70 font-semibold">Self-Introduction:</span>
                  <p className="text-cyan-100 mt-1">{selectedPartner.bio || "No description provided."}</p>
                </div>
                <div className="pt-2 border-t border-cyan-500/15 flex justify-between">
                  <span className="text-cyan-300/70">Application Date:</span>
                  <span className="text-white">{new Date(selectedPartner.appliedAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-cyan-500/20">
              <button
                onClick={() => setViewDetailsModal(false)}
                className="px-4 py-2 rounded-xl text-xs font-semibold bg-cyan-950/60 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-900/60"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: ASSIGN CROWNS & BADGES */}
      {assignModalOpen && selectedPartner && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
          onClick={() => setAssignModalOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl overflow-hidden border border-cyan-400/30 bg-[#041230] p-6 shadow-2xl space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center pb-3 border-b border-cyan-500/20">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <FaCrown className="text-amber-400" />
                <span>Assign Crown to {selectedPartner.fullName}</span>
              </h3>
              <button onClick={() => setAssignModalOpen(false)} className="text-cyan-300 hover:text-white">
                <FiX size={18} />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              {/* Crown Choice */}
              <div>
                <label className="block text-cyan-200 mb-1.5 font-semibold">Crown Badge</label>
                <select
                  value={assignForm.crownType}
                  onChange={(e) => setAssignForm({ ...assignForm, crownType: e.target.value })}
                  className="w-full bg-cyan-950/80 border border-cyan-400/30 rounded-xl px-3.5 py-2.5 text-white outline-none focus:border-cyan-300"
                >
                  {CROWN_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>
                      👑 {c.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-cyan-500/20">
              <button
                type="button"
                onClick={() => setAssignModalOpen(false)}
                className="px-4 py-2 rounded-xl text-xs font-semibold bg-cyan-950/60 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-900/60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveAssignments}
                className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-amber-500 to-orange-600 shadow-md hover:brightness-110"
              >
                Save Crown
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}